import React, { useState, useEffect } from "react";
import {
  getFunctions,
  getGrades,
  getZones,
  getJobLocations,
  getEmployeeDetails,
  getDepartments,
  getRoleType,
  getJobCategory,
  getEmployeeSuggestions,
} from "../../../services/metadataService";
import { createJobOpening } from "../../../services/jobService";
import "./PostJobForm.css";
import NormalAlert from "../../AlertComponent/NormalAlert";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useNavigate } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Modal, Button } from "react-bootstrap";

// Author: Harshada Bhavsar (18/7/2025)
// API: Create Job Opening for Employee
const PostJobForm = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    jobTitle: "",
    reportingManagerID: "",
    reportingManager: "",
    functionalManagerID: "",
    functionalManager: "",
    function: "",
    functionID: 0,
    grade: "",
    roleName: "",
    jobCategory: "",
    zone: "",
    jobLocation: "",
    offeredSalary: "",
    minimumQualification: "",
    minimumExperience: "",
    genderPreference: "",
    positionType: "",
    requisitionDate: "",
    jobDescription: "",
    department: "",
    numberOfOpenings: 0,
    roleType: "",
    jobInterviewers: [],
  });

  const [functions, setFunctions] = useState([]);
  const [grades, setGrades] = useState([]);
  const [zones, setZones] = useState([]);
  const [jobLocations, setJobLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [roleType, setRoleType] = useState([]);
  const [jobCategory, setJobCategory] = useState([]);
  const [alert, setAlert] = useState(null);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");
  const [isJobMenuOpen, setIsJobMenuOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [newInterviewer, setNewInterviewer] = useState({
    interviewerCode: "",
    interviewerName: "",
    interviewSequence: 1,
  });
  const [modalInterviewers, setModalInterviewers] = useState([]);
  const [activeField, setActiveField] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const navigate = useNavigate();
  useEffect(() => {
    const fetchData = async () => {
      setFunctions(await getFunctions());
      setGrades(await getGrades());
      setZones(await getZones());
      setRoleType(await getRoleType());
      setJobCategory(await getJobCategory());
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (formData.zone) {
      getJobLocations(formData.zone).then(setJobLocations);
    } else {
      setJobLocations([]);
    }
  }, [formData.zone]);

  // Open Modal
  const handleOpenModal = () => {
    setNewInterviewer({
      interviewerCode: "",
      interviewerName: "",
      interviewSequence: (formData.jobInterviewers.length || 0) + 1,
    });
    setShowModal(true);
  };

  // Save interviewers from modal → main form state
  const handleSaveInterviewers = () => {
    setFormData((prev) => ({
      ...prev,
      jobInterviewers: [...prev.jobInterviewers, ...modalInterviewers],
    }));
    setShowModal(false);
  };

  //Department Dropdown
  useEffect(() => {
    const fetchDepartments = async () => {
      if (formData.function) {
        const result = await getDepartments(formData.function);
        setDepartments(result || []);
        setFormData((prev) => ({
          ...prev,
          department: "", // reset when function changes
        }));
      }
    };
    fetchDepartments();
  }, [formData.function]);

  const handleChange = async (e, fieldName) => {
    if (typeof e === "string" && fieldName) {
      setFormData((prev) => ({
        ...prev,
        [fieldName]: e,
      }));
    } else {
      const { name, value } = e.target;
      let newValue = value;

      if (
        ["numberOfOpenings", "minimumExperience", "offeredSalary"].includes(
          name
        ) &&
        Number(value) < 0
      ) {
        newValue = "1";
      }

      // If it's interviewerCode, update newInterviewer instead of formData
      if (name === "interviewerCode") {
        setNewInterviewer((prev) => ({
          ...prev,
          interviewerCode: newValue,
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          [name]: newValue,
        }));
      }

      //  Handle autocomplete for interviewerCode as well
      if (
        (name === "reportingManager" ||
          name === "functionalManager" ||
          name === "interviewerCode") &&
        value.trim().length >= 3
      ) {
        try {
          const res = await getEmployeeSuggestions(value.trim());
          if (res && res.length > 0) {
            setActiveField(name);
            setSuggestions(res);
            setShowSuggestions(true);
          } else {
            setSuggestions([]);
            setShowSuggestions(false);
          }
        } catch (err) {
          console.error("Suggestion fetch error:", err);
          setSuggestions([]);
          setShowSuggestions(false);
        }
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }
  };

  const handleSelectSuggestion = (suggestion) => {
    if (activeField === "reportingManager") {
      setFormData((prev) => ({
        ...prev,
        reportingManager: `${suggestion.employeeCode} - ${suggestion.employeeName}`,
        reportingManagerID: suggestion.employeeCode,
      }));
    } else if (activeField === "functionalManager") {
      setFormData((prev) => ({
        ...prev,
        functionalManager: `${suggestion.employeeCode} - ${suggestion.employeeName}`,
        functionalManagerID: suggestion.employeeCode,
      }));
    } else if (activeField === "interviewerCode") {
      setNewInterviewer((prev) => ({
        ...prev,
        interviewerCode: suggestion.employeeCode, // only code (for backend)
        interviewerName: suggestion.employeeName, // only name
        interviewerDisplay: `${suggestion.employeeCode} - ${suggestion.employeeName}`, // for UI
      }));
    }

    setShowSuggestions(false);
    setSuggestions([]);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev < suggestions.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev > 0 ? prev - 1 : suggestions.length - 1
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
        handleSelectSuggestion(suggestions[highlightedIndex]);
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    const isEmpty = (value) =>
      value === undefined ||
      value === null ||
      (typeof value === "string" && value.trim() === "");

    if (isEmpty(formData.roleName))
      newErrors.roleName = "Job Title is required";
    if (isEmpty(formData.reportingManagerID))
      newErrors.reportingManagerID = "Reporting Manager ID is required";
    if (isEmpty(formData.functionalManagerID))
      newErrors.functionalManagerID = "Zonal Manager ID is required";
    if (isEmpty(formData.function)) newErrors.function = "Function is required";
    if (isEmpty(formData.grade)) newErrors.grade = "Grade is required";
    if (isEmpty(formData.zone)) newErrors.zone = "Zone is required";
    if (isEmpty(formData.jobLocation))
      newErrors.jobLocation = "Job Location is required";
    if (isEmpty(formData.offeredSalary))
      newErrors.offeredSalary = "Offered Salary is required";
    if (isEmpty(formData.minimumQualification))
      newErrors.minimumQualification = "Minimum Qualification is required";
    if (isEmpty(formData.minimumExperience))
      newErrors.minimumExperience = "Minimum Experience is required";
    if (isEmpty(formData.genderPreference))
      newErrors.genderPreference = "Gender Preference is required";
    if (isEmpty(formData.positionType))
      newErrors.positionType = "Position Type is required";
    if (isEmpty(formData.requisitionDate))
      newErrors.requisitionDate = "Requisition Date is required";
    if (isEmpty(formData.jobDescription))
      newErrors.jobDescription = "Job Description is required";
    if (isEmpty(formData.department))
      newErrors.department = "Department is required";
    if (isEmpty(formData.roleType))
      newErrors.roleType = "Role Type is required";
    // if (!formData.jobInterviewers || formData.jobInterviewers.length === 0) {
    //   newErrors.jobInterviewers = "At least one interviewer is required";
    // } else {
    //   formData.jobInterviewers.forEach((i, idx) => {
    //     if (!i.interviewerCode) {
    //       newErrors[`jobInterviewers_${idx}_code`] =
    //         "Interviewer Code is required";
    //     }
    //     if (!i.interviewSequence || i.interviewSequence <= 0) {
    //       newErrors[`jobInterviewers_${idx}_seq`] =
    //         "Valid Interview Sequence is required";
    //     }
    //   });
    // }

    if (
      formData.numberOfOpenings === undefined ||
      formData.numberOfOpenings === null ||
      formData.numberOfOpenings === "" ||
      formData.numberOfOpenings === 0
    ) {
      newErrors.numberOfOpenings = "Number of Openings is required";
    }
    if (isEmpty(formData.jobCategory))
      newErrors.jobCategory = "Job Category is required";
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        jobTitle: formData.roleName,
        reportingManagerID: formData.reportingManagerID,
        functionalManagerID: formData.functionalManagerID,
        function: formData.function,
        grade: formData.grade,
        zone: formData.zone,
        location: formData.jobLocation,
        offeredSalary: formData.offeredSalary,
        minimumQualification: formData.minimumQualification,
        minimumExperience: formData.minimumExperience,
        genderPreference: formData.genderPreference,
        positionType: formData.positionType,
        requisitionDate: formData.requisitionDate,
        jobDescription: formData.jobDescription,
        department: formData.department,
        numberOfOpenings: formData.numberOfOpenings,
        jobCategory: formData.jobCategory,
        roleType: formData.roleType,
        jobInterviewers: formData.jobInterviewers,
      };

      const clientErrors = validateForm();
      if (Object.keys(clientErrors).length > 0) {
        setErrors(clientErrors);
        return;
      }

      console.log("Submitting payload:", payload);

      const response = await createJobOpening(payload);
      console.log("Job created:", response);
      setAlert({ type: response.data.status, message: response.data.message });
      if (onSubmit) onSubmit(response?.data || payload);
      setTimeout(() => {
        navigate("/Dashboard/Draft-Jobs");
      }, 1500);
    } catch (error) {
      console.error("Failed to create job:", error.response?.data || error);
      setAlert({ type: "fail", message: error.response.data.message });
    }
  };

  const handleReset = () => {
    setFormData({
      jobTitle: "",
      reportingManagerID: "",
      reportingManager: "",
      functionalManagerID: "",
      functionalManager: "",
      function: "",
      functionID: 0,
      grade: "",
      roleName: "",
      jobCategory: "",
      zone: "",
      jobLocation: "",
      offeredSalary: "",
      minimumQualification: "",
      minimumExperience: "",
      genderPreference: "",
      positionType: "",
      requisitionDate: "",
      jobDescription: "",
      department: "",
      numberOfOpenings: 0,
      roleType: "",
      jobInterviewers: [],
    });
    setErrors({});
    setError("");
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(formData.jobInterviewers);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Reassign interviewSequence
    const updatedItems = items.map((item, index) => ({
      ...item,
      interviewSequence: index + 1, // Round numbers start from 1
    }));

    setFormData((prev) => ({
      ...prev,
      jobInterviewers: updatedItems,
    }));
  };

  return (
    <>
      <div className="SetuSbg"></div>
      <div className="form-over-image">
        {/* <div className="container p-5 my-5 shadow bg-white rounded role-form-container"> */}
        <main className="container my-5">
          <div className="card shadow-lg rounded-4">
            <h3 className="text-center mt-3 text-primary">CREATE NEW ROLE</h3>
            <div className="card-body p-4">
              {/* Accordion */}
              <div className="accordion" id="jobAccordion">
                <form className="row g-3" onSubmit={handleSubmit}>
                  {/* Job Details */}
                  <div className="accordion-item border rounded mb-3">
                    <h2 className="accordion-header bg-primary">
                      <button
                        className="accordion-button text-white btn-primary"
                        type="button"
                      >
                        Job Details
                      </button>
                    </h2>
                    <div id="jobDetails" className="accordion-collapse show">
                      <div className="accordion-body">
                        {/* <input type="text" placeholder="Job Title" className="form-control mb-3" /> */}
                        {/* Job Title */}
                        <div className="mb-2">
                          <label className="form-label text-black">
                            Job Title <span style={{ color: "red" }}> *</span>
                          </label>
                          <input
                            name="roleName"
                            className={`form-control ${
                              errors.roleName ? "is-invalid" : ""
                            }`}
                            value={formData.roleName || ""}
                            onChange={handleChange}
                          />
                          {errors.roleName && (
                            <div className="invalid-feedback">
                              {errors.roleName}
                            </div>
                          )}
                        </div>

                        {/* Job Description */}
                        <div className="col-md-12 mb-3">
                          <label className="form-label text-black">
                            Job Description{" "}
                            <span style={{ color: "red" }}> *</span>
                          </label>
                          <ReactQuill
                            theme="snow"
                            name="jobDescription"
                            placeholder="Job Description"
                            className={`quill-editor mb-5 ${
                              errors.jobDescription ? "is-invalid" : ""
                            }`}
                            style={{ height: "200px" }}
                            value={formData.jobDescription || ""}
                            onChange={(content) =>
                              handleChange(content, "jobDescription")
                            }
                          />

                          {errors.jobDescription && (
                            <div className="invalid-feedback">
                              {errors.jobDescription}
                            </div>
                          )}
                        </div>

                        <div className="accordion-body row g-3">
                          {/* Job Category */}
                          <div className="col-md-6 mb-3">
                            <label className="form-label text-black">
                              Job Category{" "}
                              <span style={{ color: "red" }}> *</span>
                            </label>

                            <div className="position-relative">
                              <select
                                name="jobCategory"
                                className={`input100 form-control pe-5 ${
                                  errors.jobCategory ? "is-invalid" : ""
                                }`}
                                value={formData.jobCategory || ""}
                                onChange={handleChange}
                              >
                                <option value="">Select</option>
                                {jobCategory.map((j, index) => (
                                  <option
                                    key={j.jobCategoryID || index}
                                    value={j.jobCategoryID}
                                  >
                                    {j.jobCategory}
                                  </option>
                                ))}
                              </select>
                              <i
                                // className="bi bi-chevron-down position-absolute"
                                className={`ms-3 position-absolute bi ${
                                  isJobMenuOpen
                                    ? "bi-chevron-up"
                                    : "bi-chevron-down"
                                }`}
                                style={{
                                  right: "10px",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  pointerEvents: "none",
                                  color: "#555",
                                }}
                              ></i>
                              {errors.jobCategory && (
                                <div className="invalid-feedback">
                                  {errors.jobCategory}
                                </div>
                              )}
                            </div>
                          </div>
                          {/* Position Type */}
                          <div className="col-md-6 mb-3">
                            <label className="form-label text-black">
                              Position Type{" "}
                              <span style={{ color: "red" }}> *</span>
                            </label>
                            <div className="position-relative">
                              <select
                                name="positionType"
                                className={`input100  form-control ${
                                  errors.positionType ? "is-invalid" : ""
                                }`}
                                value={formData.positionType || ""}
                                onChange={handleChange}
                              >
                                <option value="">Select</option>
                                <option value="Replacement">Replacement</option>
                                <option value="New_Opening">New Opening</option>
                              </select>
                              <i
                                // className="bi bi-chevron-down position-absolute"
                                className={`ms-3 position-absolute bi ${
                                  isJobMenuOpen
                                    ? "bi-chevron-up"
                                    : "bi-chevron-down"
                                }`}
                                style={{
                                  right: "10px",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  pointerEvents: "none",
                                  color: "#555",
                                }}
                              ></i>
                              {errors.positionType && (
                                <div className="invalid-feedback">
                                  {errors.positionType}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        {/* Requisition Date */}
                        <div className="mb-3">
                          <label className="form-label text-black">
                            Requisition Date{" "}
                            <span style={{ color: "red" }}> *</span>
                          </label>
                          <input
                            type="date"
                            name="requisitionDate"
                            value={formData.requisitionDate || ""}
                            onChange={handleChange}
                            max={new Date().toISOString().split("T")[0]} // Prevent future dates
                            className={`form-control ${
                              errors.requisitionDate ? "is-invalid" : ""
                            }`}
                          />
                          {errors.requisitionDate && (
                            <div className="invalid-feedback">
                              {errors.requisitionDate}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Organizational Information */}
                  <div className="accordion-item border rounded mb-3">
                    <h2 className="accordion-header">
                      <button
                        className="accordion-button text-white btn-primary"
                        type="button"
                      >
                        Organizational Information
                      </button>
                    </h2>
                    <div id="orgInfo" className="accordion-collapse show">
                      <div className="accordion-body row g-3">
                        {/* Function */}
                        <div className="col-md-6 mb-3">
                          <label className="form-label text-black">
                            Department <span style={{ color: "red" }}> *</span>
                          </label>

                          <div className="position-relative">
                            <select
                              name="function"
                              className={`input100 form-control pe-5 ${
                                errors.function ? "is-invalid" : ""
                              }`}
                              value={formData.function || ""}
                              onChange={handleChange}
                            >
                              <option value="">Select</option>
                              {functions.map((f, index) => (
                                <option
                                  key={f.functionID || index}
                                  value={f.functionID}
                                >
                                  {f.functionName}
                                </option>
                              ))}
                            </select>
                            <i
                              // className="bi bi-chevron-down position-absolute"
                              className={`ms-3 position-absolute bi ${
                                isJobMenuOpen
                                  ? "bi-chevron-up"
                                  : "bi-chevron-down"
                              }`}
                              style={{
                                right: "10px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                pointerEvents: "none",
                                color: "#555",
                              }}
                            ></i>
                            {errors.function && (
                              <div className="invalid-feedback">
                                {errors.function}
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Functional Manager ID */}
                        <div className="col-md-6 position-relative mb-3">
                          <label className="form-label text-black">
                            Zonal Manager{" "}
                            <span style={{ color: "red" }}> *</span>
                          </label>
                          <input
                            name="functionalManager"
                            className={`form-control  ${
                              errors.functionalManagerID ? "is-invalid" : ""
                            }`}
                            value={formData.functionalManager || ""}
                            onChange={handleChange}
                            onBlur={() =>
                              setTimeout(() => setShowSuggestions(false), 200)
                            } // hide dropdown after blur
                            autoComplete="off"
                            onKeyDown={(e) => handleKeyDown(e)}
                          />
                          {errors.functionalManagerID && (
                            <div className="invalid-feedback">
                              {errors.functionalManagerID}
                            </div>
                          )}

                          {/* Suggestions dropdown */}
                          {showSuggestions &&
                            activeField === "functionalManager" &&
                            suggestions.length > 0 && (
                              <ul
                                className="list-group position-absolute w-100"
                                style={{ zIndex: 1000 }}
                              >
                                {suggestions.map((s, idx) => (
                                  <li
                                    key={idx}
                                    className={`list-group-item list-group-item-action ${
                                      highlightedIndex === idx ? "active" : ""
                                    }`}
                                    onClick={() => handleSelectSuggestion(s)}
                                    style={{ cursor: "pointer" }}
                                  >
                                    {s.employeeCode} - {s.employeeName}
                                  </li>
                                ))}
                              </ul>
                            )}
                        </div>
                        {/* Department */}
                        <div className="col-md-6 mb-3">
                          <label className="form-label text-black">
                            Profile
                            <span style={{ color: "red" }}> *</span>
                          </label>

                          <div className="position-relative">
                            <select
                              name="department"
                              className={`input100 form-control pe-5 ${
                                errors.department ? "is-invalid" : ""
                              }`}
                              value={formData.department || ""}
                              onChange={handleChange}
                            >
                              <option value="">Select</option>
                              {departments.map((dept, index) => (
                                <option
                                  key={dept.departmentID || index}
                                  value={dept.departmentID}
                                >
                                  {dept.departmentName}
                                </option>
                              ))}
                            </select>
                            <i
                              // className="bi bi-chevron-down position-absolute"
                              className={`ms-3 position-absolute bi ${
                                isJobMenuOpen
                                  ? "bi-chevron-up"
                                  : "bi-chevron-down"
                              }`}
                              style={{
                                right: "10px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                pointerEvents: "none",
                                color: "#555",
                              }}
                            ></i>
                            {errors.department && (
                              <div className="invalid-feedback">
                                {errors.department}
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Reporting Manager ID */}
                        <div className="col-md-6 position-relative mb-3">
                          <label className="form-label text-black">
                            Reporting Manager{" "}
                            <span style={{ color: "red" }}> *</span>
                          </label>
                          <input
                            name="reportingManager"
                            className={`form-control  ${
                              errors.reportingManagerID ? "is-invalid" : ""
                            }`}
                            value={formData.reportingManager || ""}
                            onChange={handleChange}
                            onBlur={() =>
                              setTimeout(() => setShowSuggestions(false), 200)
                            }
                            onKeyDown={(e) => handleKeyDown(e)}
                            autoComplete="off"
                          />
                          {errors.reportingManagerID && (
                            <div className="invalid-feedback">
                              {errors.reportingManagerID}
                            </div>
                          )}

                          {/* Suggestions dropdown */}
                          {showSuggestions &&
                            activeField === "reportingManager" &&
                            suggestions.length > 0 && (
                              <ul
                                className="list-group position-absolute w-100"
                                style={{ zIndex: 1000 }}
                              >
                                {suggestions.map((s, idx) => (
                                  <li
                                    key={idx}
                                    className={`list-group-item list-group-item-action ${
                                      highlightedIndex === idx ? "active" : ""
                                    }`}
                                    onClick={() => handleSelectSuggestion(s)}
                                    style={{ cursor: "pointer" }}
                                  >
                                    {s.employeeCode} - {s.employeeName}
                                  </li>
                                ))}
                              </ul>
                            )}
                        </div>
                        {/* Grade */}
                        <div className="col-md-6 mb-3">
                          <label className="form-label text-black">
                            Grade <span style={{ color: "red" }}> *</span>
                          </label>
                          <div className="position-relative">
                            <select
                              name="grade"
                              className={`input100  form-control ${
                                errors.grade ? "is-invalid" : ""
                              }`}
                              value={formData.grade || ""}
                              onChange={handleChange}
                            >
                              <option value="">Select</option>
                              {grades.map((g, index) => (
                                <option key={g.grade || index} value={g.grade}>
                                  {g.gradeName}
                                </option>
                              ))}
                            </select>
                            <i
                              // className="bi bi-chevron-down position-absolute"
                              className={`ms-3 position-absolute bi ${
                                isJobMenuOpen
                                  ? "bi-chevron-up"
                                  : "bi-chevron-down"
                              }`}
                              style={{
                                right: "10px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                pointerEvents: "none",
                                color: "#555",
                              }}
                            ></i>
                            {errors.grade && (
                              <div className="invalid-feedback">
                                {errors.grade}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Location Details */}
                  <div className="accordion-item border rounded mb-3">
                    <h2 className="accordion-header">
                      <button
                        className="accordion-button text-white btn-primary"
                        type="button"
                      >
                        Location Details
                      </button>
                    </h2>
                    <div
                      id="locationDetails"
                      className="accordion-collapse show"
                    >
                      <div className="accordion-body row g-3">
                        {/* Zone */}
                        <div className="col-md-6 mb-3">
                          <label className="form-label text-black">
                            Zone <span style={{ color: "red" }}> *</span>
                          </label>
                          <div className="position-relative">
                            <select
                              name="zone"
                              className={`input100  form-control ${
                                errors.zone ? "is-invalid" : ""
                              }`}
                              value={formData.zone || ""}
                              onChange={handleChange}
                            >
                              <option value="">Select</option>
                              {zones.map((z, index) => (
                                <option
                                  key={z.zoneID || index}
                                  value={z.zoneID}
                                >
                                  {z.zoneName}
                                </option>
                              ))}
                            </select>
                            <i
                              // className="bi bi-chevron-down position-absolute"
                              className={`ms-3 position-absolute bi ${
                                isJobMenuOpen
                                  ? "bi-chevron-up"
                                  : "bi-chevron-down"
                              }`}
                              style={{
                                right: "10px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                pointerEvents: "none",
                                color: "#555",
                              }}
                            ></i>
                            {errors.zone && (
                              <div className="invalid-feedback">
                                {errors.zone}
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Job Location */}
                        <div className="col-md-6 mb-3">
                          <label className="form-label text-black">
                            Job Location{" "}
                            <span style={{ color: "red" }}> *</span>
                          </label>
                          <div className="position-relative">
                            <select
                              name="jobLocation"
                              className={`input100  form-control ${
                                errors.jobLocation ? "is-invalid" : ""
                              }`}
                              value={formData.jobLocation || ""}
                              onChange={handleChange}
                            >
                              <option value="">Select</option>
                              {jobLocations.map((j, index) => (
                                <option
                                  key={j.locationID || index}
                                  value={j.locationID}
                                >
                                  {j.jobLocation}
                                </option>
                              ))}
                            </select>
                            <i
                              // className="bi bi-chevron-down position-absolute"
                              className={`ms-3 position-absolute bi ${
                                isJobMenuOpen
                                  ? "bi-chevron-up"
                                  : "bi-chevron-down"
                              }`}
                              style={{
                                right: "10px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                pointerEvents: "none",
                                color: "#555",
                              }}
                            ></i>
                            {errors.jobLocation && (
                              <div className="invalid-feedback">
                                {errors.jobLocation}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Candidate Requirements */}
                  <div className="accordion-item border rounded mb-3">
                    <h2 className="accordion-header">
                      <button
                        className="accordion-button text-white btn-primary"
                        type="button"
                      >
                        Candidate Requirements
                      </button>
                    </h2>
                    <div id="candidateReq" className="accordion-collapse show">
                      <div className="accordion-body row g-3">
                        {/* Minimum Qualification */}
                        <div className="col-md-6 mb-3">
                          <label className="form-label text-black">
                            Minimum Qualification{" "}
                            <span style={{ color: "red" }}> *</span>
                          </label>
                          <input
                            name="minimumQualification"
                            className={`form-control ${
                              errors.minimumQualification ? "is-invalid" : ""
                            }`}
                            value={formData.minimumQualification || ""}
                            onChange={handleChange}
                          />
                          {errors.minimumQualification && (
                            <div className="invalid-feedback">
                              {errors.minimumQualification}
                            </div>
                          )}
                        </div>
                        {/*  Minimum Experience */}
                        <div className="col-md-6 mb-3">
                          <label className="form-label text-black">
                            Minimum Experience (in years){" "}
                            <span style={{ color: "red" }}> *</span>
                          </label>
                          <input
                            name="minimumExperience"
                            className={`form-control ${
                              errors.minimumExperience ? "is-invalid" : ""
                            }`}
                            value={formData.minimumExperience || ""}
                            type="number"
                            min="0"
                            max="99"
                            onChange={(e) => {
                              let val = e.target.value;
                              if (val.length > 2) val = val.slice(0, 2); // trim to 2 digits
                              setFormData((prev) => ({
                                ...prev,
                                minimumExperience: val,
                              }));
                            }}
                          />
                          {errors.minimumExperience && (
                            <div className="invalid-feedback">
                              {errors.minimumExperience}
                            </div>
                          )}
                        </div>
                        {/* Gender Preference */}
                        <div className="col-md-6 mb-3">
                          <label className="form-label text-black">
                            Gender Preference
                            <span style={{ color: "red" }}> *</span>
                          </label>

                          <div className="position-relative">
                            <select
                              name="genderPreference"
                              className={`input100 form-control pe-5 ${
                                errors.genderPreference ? "is-invalid" : ""
                              }`}
                              value={formData.genderPreference || ""}
                              onChange={handleChange}
                            >
                              <option value="">Select</option>
                              <option value="Any">Any</option>
                              <option value="Male">Male</option>
                              <option value="Female">Female</option>
                            </select>
                            <i
                              // className="bi bi-chevron-down position-absolute"
                              className={`ms-3 position-absolute bi ${
                                isJobMenuOpen
                                  ? "bi-chevron-up"
                                  : "bi-chevron-down"
                              }`}
                              style={{
                                right: "10px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                pointerEvents: "none",
                                color: "#555",
                              }}
                            ></i>
                            {errors.genderPreference && (
                              <div className="invalid-feedback">
                                {errors.genderPreference}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Role Type */}
                        <div className="col-md-6 mb-3">
                          <label className="form-label text-black">
                            Role Type
                            <span style={{ color: "red" }}> *</span>
                          </label>

                          <div className="position-relative">
                            <select
                              name="roleType"
                              className={`input100 form-control pe-5 ${
                                errors.roleType ? "is-invalid" : ""
                              }`}
                              value={formData.roleType || ""}
                              onChange={handleChange}
                            >
                              <option value="">Select</option>
                              {roleType.map((r, index) => (
                                <option
                                  key={r.roleTypeID || index}
                                  value={r.roleTypeID}
                                >
                                  {r.roleType}
                                </option>
                              ))}
                            </select>
                            <i
                              // className="bi bi-chevron-down position-absolute"
                              className={`ms-3 position-absolute bi ${
                                isJobMenuOpen
                                  ? "bi-chevron-up"
                                  : "bi-chevron-down"
                              }`}
                              style={{
                                right: "10px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                pointerEvents: "none",
                                color: "#555",
                              }}
                            ></i>
                            {errors.roleType && (
                              <div className="invalid-feedback">
                                {errors.roleType}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Compensation & Openings */}
                  <div className="accordion-item border rounded mb-3">
                    <h2 className="accordion-header">
                      <button
                        className="accordion-button text-white btn-primary"
                        type="button"
                      >
                        Compensation & Openings
                      </button>
                    </h2>
                    <div id="compensation" className="accordion-collapse show">
                      <div className="accordion-body row g-3">
                        {/* Other Fields */}
                        <div className="col-md-6">
                          <label className="form-label text-black">
                            Number of Openings{" "}
                            <span style={{ color: "red" }}> *</span>
                          </label>
                          <input
                            name="numberOfOpenings"
                            className={`form-control ${
                              errors.numberOfOpenings ? "is-invalid" : ""
                            }`}
                            value={formData.numberOfOpenings || ""}
                            type="number"
                            min="0"
                            max="999"
                            onChange={(e) => {
                              let val = e.target.value;
                              if (val.length > 3) val = val.slice(0, 3); // trim to 3 digits
                              setFormData((prev) => ({
                                ...prev,
                                numberOfOpenings: val,
                              }));
                            }}
                          />
                          {errors.numberOfOpenings && (
                            <div className="invalid-feedback">
                              {errors.numberOfOpenings}
                            </div>
                          )}
                        </div>
                        <div className="col-md-6">
                          <label className="form-label text-black">
                            Maximum Budget{" "}
                            <span style={{ color: "red" }}> *</span>
                          </label>
                          <input
                            name="offeredSalary"
                            className={`form-control ${
                              errors.offeredSalary ? "is-invalid" : ""
                            }`}
                            value={formData.offeredSalary || ""}
                            type="number"
                            onChange={handleChange}
                          />
                          {errors.offeredSalary && (
                            <div className="invalid-feedback">
                              {errors.offeredSalary}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Interview Panel */}
                  <div className="accordion-item border rounded mb-3">
                    <h2 className="accordion-header">
                      <button
                        className="accordion-button text-white btn-primary"
                        type="button"
                      >
                        Interview Panel
                      </button>
                    </h2>
                    <div
                      id="interviewPanel"
                      className="accordion-collapse show"
                    >
                      <div className="accordion-body">
                        <div id="interviewer-list" className="mb-3"></div>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleOpenModal}
                        >
                          ➕ Add Interviewer
                        </button>

                        {/* Show saved interviewers (after Save All) */}
                        {/* for drag and down install this - npm install @hello-pangea/dnd */}

                        <DragDropContext onDragEnd={handleDragEnd}>
                          <Droppable droppableId="interviewers-list">
                            {(provided) => (
                              <ul
                                className="list-group"
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                              >
                                {formData.jobInterviewers.map((intv, index) => (
                                  <Draggable
                                    key={intv.interviewerCode.toString()}
                                    draggableId={intv.interviewerCode.toString()}
                                    index={index}
                                  >
                                    {(provided) => (
                                      <li
                                        className="list-group-item d-flex justify-content-between align-items-center"
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                      >
                                        {intv.interviewerDisplay ||
                                          `${intv.interviewerCode} - ${intv.interviewerName}`}{" "}
                                        (Round: {intv.interviewSequence})
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-danger"
                                          onClick={() =>
                                            setFormData((prev) => ({
                                              ...prev,
                                              jobInterviewers:
                                                prev.jobInterviewers.filter(
                                                  (_, idx) => idx !== index
                                                ),
                                            }))
                                          }
                                        >
                                          <i className="bi bi-trash"></i>
                                        </button>
                                      </li>
                                    )}
                                  </Draggable>
                                ))}
                                {provided.placeholder}
                              </ul>
                            )}
                          </Droppable>
                        </DragDropContext>

                        {formData.jobInterviewers.length === 0 && (
                          <li className="list-group-item text-muted text-center">
                            No interviewers added yet.
                          </li>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="text-center mt-4">
                    <button type="submit" className="btn btn-primary px-5 py-2">
                      GENERATE ROLE
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary mx-4 px-5 py-2"
                      onClick={handleReset}
                    >
                      Reset
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </main>
      </div>
      {/* </div> */}

      {/* Add Interviewer Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        backdrop="static" // prevents accidental close on backdrop click
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Interviewer</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* Interviewer Code */}
          <div className="mb-3 position-relative">
            <label className="form-label">Interviewer Code</label>
            <input
              type="text"
              name="interviewerCode"
              className="form-control"
              value={
                newInterviewer.interviewerDisplay ||
                newInterviewer.interviewerCode
              }
              onChange={handleChange}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              onKeyDown={(e) => handleKeyDown(e)}
              placeholder="Enter Interviewer Code"
              autoComplete="off"
            />

            {/* Suggestions dropdown */}
            {showSuggestions &&
              activeField === "interviewerCode" &&
              suggestions.length > 0 && (
                <ul
                  className="list-group position-absolute w-100"
                  style={{ zIndex: 2000 }}
                >
                  {suggestions.map((s, idx) => (
                    <li
                      key={idx}
                      className={`list-group-item list-group-item-action ${
                        highlightedIndex === idx ? "active" : ""
                      }`}
                      onClick={() => handleSelectSuggestion(s)}
                      style={{ cursor: "pointer" }}
                    >
                      {s.employeeCode} - {s.employeeName}
                    </li>
                  ))}
                </ul>
              )}
          </div>

          {/* Interview Round */}
          <div className="mb-3">
            <label className="form-label">Interview Round</label>
            <input
              type="number"
              min="1"
              className="form-control"
              value={newInterviewer.interviewSequence}
              onChange={(e) =>
                setNewInterviewer((prev) => ({
                  ...prev,
                  interviewSequence: Number(e.target.value),
                }))
              }
            />
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              if (
                newInterviewer.interviewerCode &&
                newInterviewer.interviewerName
              ) {
                setFormData((prev) => {
                  const updatedList = [
                    ...prev.jobInterviewers,
                    {
                      interviewerCode: newInterviewer.interviewerCode, // code (for backend)
                      interviewerName: newInterviewer.interviewerName, // name (for backend clarity)
                      interviewerDisplay: `${newInterviewer.interviewerCode} - ${newInterviewer.interviewerName}`, // for UI
                      interviewSequence: prev.jobInterviewers.length + 1,
                    },
                  ];

                  return {
                    ...prev,
                    jobInterviewers: updatedList,
                  };
                });

                // reset new interviewer
                setNewInterviewer({
                  interviewerCode: "",
                  interviewerName: "",
                  interviewSequence: (formData.jobInterviewers.length || 0) + 1,
                });

                setShowModal(false);
              } else {
                alert("Please enter valid interviewer code");
              }
            }}
          >
            Add
          </Button>
        </Modal.Footer>
      </Modal>

      {alert && (
        <NormalAlert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}
    </>
  );
};

export default PostJobForm;
